import { useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Drawer from "../../../components/ui/Drawer";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useToast } from "../../../components/ui/Toast";
import { useGetGroupProgressQuery, useLeaveMyGroupMutation } from "../../../api/groupsApi";
import { SoftButton, cls } from "../../hatim/soft/SoftKit";
import LeaveGroupConfirm from "../../groups/components/LeaveGroupConfirm";
import GroupRankingView from "../../groups/components/insights/GroupRankingView";
import GroupHighlightsView from "../../groups/components/insights/GroupHighlightsView";
import GroupTitlesView from "../../groups/components/insights/GroupTitlesView";

// Gruplarım sekmeleri (mobildeki sırayla): İstatistik, Olaylar, Ünvanlar.
const TABS = ["İstatistik", "Olaylar", "Ünvanlar"];

function Stat({ value, label }) {
  return (
    <div className="flex-1 min-w-0 text-center">
      <div className={`text-xl font-medium ${cls.heading}`}>{value}</div>
      <div className={`text-[13px] truncate ${cls.muted}`}>{label}</div>
    </div>
  );
}

/**
 * Terakkim'de gruba basınca açılan çekmece: üstte bu haftaki durumum (sıra,
 * tamamlama, seri, tam gün), altta grubun sıralaması, olayları ve ünvanları,
 * grup açıklaması ve gruptan ayrılma. Ayrı bir sayfaya gitmeye gerek kalmaz.
 */
export default function GroupStatusDrawer({ group, range, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [leaveGroup, { isLoading: leaving }] = useLeaveMyGroupMutation();

  useEffect(() => {
    if (group) setTab(0);
  }, [group]);

  const { data } = useGetGroupProgressQuery(
    { groupId: group?.id, startDate: range.start, endDate: range.end },
    { skip: !group }
  );
  const members = data?.data?.members || [];
  const me = members.find((m) => m.isMe) || null;

  const handleLeave = async () => {
    try {
      await leaveGroup({ groupId: group.id }).unwrap();
      toast.success(t("group_leave_success"));
      setConfirmOpen(false);
      onClose();
    } catch (err) {
      toast.error(err?.message || err?.data?.message || t("group_leave_error"));
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Drawer
        open={!!group}
        onClose={onClose}
        width="md"
        title={group?.groupName || ""}
        subtitle={group ? `${group.memberCount} ${t("mygroups_members")}` : ""}
        titleIcon={<GroupsOutlinedIcon />}
        footer={
          <div className="flex gap-2">
            <SoftButton tone="cancel" fullWidth onClick={() => setConfirmOpen(true)}>
              {t("group_leave_cta")}
            </SoftButton>
            <SoftButton tone="soft" fullWidth onClick={onClose}>
              {t("manage_goals_close")}
            </SoftButton>
          </div>
        }
      >
        {group ? (
          <div className="flex flex-col">
            {/* Bu haftaki durumum: açık zeminli sabit özet */}
            <div className={`px-5 py-4 ${cls.summary}`}>
              <div className={`text-[13px] mb-2 ${cls.muted}`}>{`${t("mygroups_title")} · ${t("mygroups_week")}`}</div>
              <div className="flex items-center gap-2">
                <Stat value={me ? `${me.rank}/${members.length}` : "·"} label={t("mygroups_rank")} />
                <Stat value={`%${me ? Math.round(me.completionRate || 0) : 0}`} label={t("mygroups_completion")} />
                <Stat value={me?.streak || 0} label={t("mygroups_streak")} />
                <Stat value={me?.perfectDays || 0} label={t("mygroups_perfect")} />
              </div>
            </div>

            <div className="border-b border-terakkiBorder dark:border-white/15 px-2">
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
                {TABS.map((label) => (
                  <Tab key={label} label={t(label)} sx={{ minHeight: 46, textTransform: "none", fontSize: 15, fontWeight: 500 }} />
                ))}
              </Tabs>
            </div>
            <div className="h-[520px] flex flex-col">
              {tab === 0 ? (
                <GroupRankingView groupId={String(group.id)} />
              ) : tab === 1 ? (
                <GroupHighlightsView groupId={String(group.id)} />
              ) : (
                <GroupTitlesView groupId={String(group.id)} />
              )}
            </div>

            {group.description ? (
              <div className="px-5 py-4 border-t border-terakkiBorder dark:border-white/15">
                <div className={`text-base mb-1 ${cls.muted}`}>{t("group_description_title")}</div>
                <p className={`text-[15px] ${cls.title}`}>{group.description}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Drawer>
      <LeaveGroupConfirm
        open={confirmOpen}
        loading={leaving}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleLeave}
      />
    </>
  );
}
